import React, { useState, useEffect } from 'react';
import { useGMB } from '../contexts/GMBContext';
import { postsAPI } from '../lib/api';
import { 
  Calendar, Clock, Trash2, Eye, MousePointer, 
  MoreHorizontal, CheckCircle, AlertCircle, Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const ScheduledPostsPage = () => {
  const { gmbProfile, isConnected } = useGMB();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await postsAPI.getScheduled();
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (postId) => {
    try {
      await postsAPI.delete(postId);
      setPosts(prev => prev.filter(p => p.id !== postId));
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'posted':
        return { color: 'text-[#1E8E3E]', bg: 'bg-[#E6F4EA]', icon: <CheckCircle className="w-4 h-4" />, label: 'Posted' };
      case 'failed':
        return { color: 'text-[#D93025]', bg: 'bg-[#FCE8E6]', icon: <AlertCircle className="w-4 h-4" />, label: 'Failed' };
      default:
        return { color: 'text-[#F9AB00]', bg: 'bg-[#FEF7E0]', icon: <Clock className="w-4 h-4" />, label: 'Pending' };
    }
  };

  // Combine saved posts with mock recent posts from GMB profile
  const allPosts = isConnected && gmbProfile ? [
    ...posts.map(p => ({ ...p, source: 'saved' })),
    ...gmbProfile.recentPosts.map(p => ({ ...p, source: 'published', status: 'posted' }))
  ] : posts;

  const filteredPosts = filter === 'all' ? allPosts : allPosts.filter(p => p.status === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-[#E8F0FE] rounded-xl flex items-center justify-center">
            <Calendar className="w-6 h-6 text-[#1A73E8]" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
              Scheduled & Published Posts
            </h1>
            <p className="text-[#5F6368]">Manage your GMB posts and view performance</p>
          </div>
        </div>
      </div>

      {/* Connected Profile */}
      {isConnected && gmbProfile && (
        <div className="dashboard-card bg-[#F8F9FA]">
          <div className="flex items-center gap-4">
            <img 
              src={gmbProfile.profileImage} 
              alt={gmbProfile.name}
              className="w-12 h-12 rounded-lg object-cover"
            />
            <div>
              <p className="font-medium text-[#202124]">Posts for: {gmbProfile.name}</p>
              <p className="text-sm text-[#5F6368]">{allPosts.length} total posts</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2">
        {['all', 'pending', 'posted', 'failed'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f 
                ? 'bg-[#1A73E8] text-white' 
                : 'bg-[#F1F3F4] text-[#5F6368] hover:bg-[#E8EAED]'
            }`}
            data-testid={`filter-${f}`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Posts List */}
      {filteredPosts.length === 0 ? (
        <div className="dashboard-card text-center py-12">
          <Calendar className="w-12 h-12 mx-auto text-[#DADCE0]" />
          <p className="mt-4 text-[#5F6368]">No posts found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPosts.map((post) => {
            const statusConfig = getStatusConfig(post.status);
            return (
              <div key={post.id} className="dashboard-card">
                <div className="flex gap-4">
                  {/* Post Image */}
                  {post.image ? (
                    <img 
                      src={post.image} 
                      alt="Post"
                      className="w-32 h-32 rounded-lg object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-32 h-32 bg-[#F8F9FA] rounded-lg flex items-center justify-center flex-shrink-0">
                      <ImageIcon className="w-8 h-8 text-[#DADCE0]" />
                    </div>
                  )}

                  {/* Post Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="text-[#202124] line-clamp-2">
                          {post.post_content || post.content}
                        </p>
                        
                        {/* Meta Info */}
                        <div className="flex items-center gap-4 mt-3 text-sm text-[#5F6368]">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {post.scheduled_time 
                              ? format(new Date(post.scheduled_time), 'MMM d, yyyy') 
                              : post.date 
                                ? format(new Date(post.date), 'MMM d, yyyy')
                                : 'Not scheduled'}
                          </span>
                          {post.views !== undefined && (
                            <span className="flex items-center gap-1">
                              <Eye className="w-4 h-4" />
                              {post.views.toLocaleString()} views
                            </span>
                          )}
                          {post.clicks !== undefined && (
                            <span className="flex items-center gap-1">
                              <MousePointer className="w-4 h-4" />
                              {post.clicks} clicks
                            </span>
                          )}
                        </div>

                        {/* Tags */}
                        <div className="flex items-center gap-2 mt-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${statusConfig.bg} ${statusConfig.color}`}>
                            {statusConfig.icon}
                            {statusConfig.label}
                          </span>
                          {post.language && (
                            <span className="px-2 py-1 bg-[#F1F3F4] text-[#5F6368] text-xs rounded">
                              {post.language}
                            </span>
                          )}
                          {post.tone && (
                            <span className="px-2 py-1 bg-[#F1F3F4] text-[#5F6368] text-xs rounded">
                              {post.tone}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      {post.source !== 'published' && (
                        <button
                          onClick={() => handleDelete(post.id)}
                          className="p-2 text-[#5F6368] hover:text-[#D93025] hover:bg-[#FCE8E6] rounded-lg transition-colors"
                          data-testid={`delete-post-${post.id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ScheduledPostsPage;
